// Admin tour list with status badges, status filter, search, drag-and-drop reordering
// Shows concept/published status visually; concept tours are dimmed

import React, { useEffect, useState } from 'react';
import { Pencil as Edit, Trash2, Plus, Clock, MapPin, DollarSign, Activity, Search, GripVertical, TreePine, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Tour, TourStatus } from '../../types/tours';
import { Link } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableTourItemProps {
  tour: Tour;
  onDelete: (id: string) => void;
}

const SortableTourItem: React.FC<SortableTourItemProps> = ({ tour, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: tour.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const isConcept = tour.status === 'concept';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group p-6 relative ${isConcept ? 'opacity-70' : ''}`}
    >
      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div {...attributes} {...listeners} className="cursor-grab">
              <GripVertical className="w-5 h-5 text-white/40" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold truncate group-hover:text-[#E85C0D] transition-colors">
                  {tour.name}
                </h3>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                  isConcept
                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                    : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isConcept ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                  {isConcept ? 'Concept' : 'Published'}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <div className="flex flex-col gap-1">
                  {(tour.group_tour_price != null && tour.group_tour_price > 0) && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#E85C0D]" />
                      <span>Group: ${tour.group_tour_price}</span>
                    </div>
                  )}
                  {(tour.reduced_group_price != null && tour.reduced_group_price > 0) && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#E85C0D]" />
                      <span>Reduced: ${tour.reduced_group_price}</span>
                    </div>
                  )}
                  {(tour.private_price != null && tour.private_price > 0) && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#E85C0D]" />
                      <span>Private: ${tour.private_price}</span>
                    </div>
                  )}
                  {!tour.group_tour_price && !tour.reduced_group_price && !tour.private_price && (
                    <span className="text-white/30 italic text-xs">No pricing set</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {tour.location && (
                    <div className="flex items-center gap-2">
                      <TreePine className="w-4 h-4 text-[#E85C0D]" />
                      <span>{tour.location}</span>
                    </div>
                  )}
                  {tour.meeting_point && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#E85C0D]" />
                      <span>{tour.meeting_point}</span>
                    </div>
                  )}
                  {tour.duration && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#E85C0D]" />
                      <span>{tour.duration}</span>
                    </div>
                  )}
                  {tour.activity_level && (
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#E85C0D]" />
                      <span className="capitalize">{tour.activity_level}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 ml-4">
            <Link
              to={`/kh-admin/tours/${tour.id}/edit`}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
            >
              <Edit className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" />
            </Link>
            <button
              onClick={() => onDelete(tour.id)}
              className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 transition-all duration-300 transform hover:scale-105"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

type StatusFilter = 'all' | TourStatus;

const TourList = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [filteredTours, setFilteredTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchTours();
  }, []);

  useEffect(() => {
    let result = tours;
    if (statusFilter !== 'all') {
      result = result.filter(t => t.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.location || '').toLowerCase().includes(q)
      );
    }
    setFilteredTours(result);
  }, [search, tours, statusFilter]);

  const fetchTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTours(data || []);
    } catch (err) {
      setError('Failed to fetch tours');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTours((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });

      try {
        const updates = tours.map((tour, index) => ({
          ...tour,
          display_order: index,
          updated_at: new Date().toISOString()
        }));

        const { error } = await supabase
          .from('tours')
          .upsert(updates, { onConflict: 'id' });

        if (error) throw error;
      } catch (err) {
        console.error('Error updating tour order:', err);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tour?')) return;

    try {
      const { error } = await supabase
        .from('tours')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setTours(tours.filter(tour => tour.id !== id));
    } catch (err) {
      console.error('Error deleting tour:', err);
      alert('Failed to delete tour');
    }
  };

  const conceptCount = tours.filter(t => t.status === 'concept').length;
  const publishedCount = tours.filter(t => t.status === 'published').length;

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Tours</h2>
          <p className="text-sm text-white/70 mt-1">
            {publishedCount} published, {conceptCount} concept{conceptCount !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/kh-admin/tours/new"
          className="flex items-center gap-2 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Tour
        </Link>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Search tours by name or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#1F2B1F] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/40"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="pl-9 pr-8 py-3 bg-[#1F2B1F] border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white appearance-none cursor-pointer"
          >
            <option value="all" className="bg-[#1F2B1F]">All</option>
            <option value="published" className="bg-[#1F2B1F]">Published</option>
            <option value="concept" className="bg-[#1F2B1F]">Concept</option>
          </select>
        </div>
      </div>

      <div className="bg-[#1F2B1F] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredTours}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-white/10">
                {filteredTours.length === 0 ? (
                  <div className="p-8 text-center text-white/50">No tours found</div>
                ) : (
                  filteredTours.map((tour) => (
                    <SortableTourItem
                      key={tour.id}
                      tour={tour}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default TourList;
